import React from 'react';
import { motion } from 'framer-motion';
import { FacebookIcon, InstagramIcon, YoutubeIcon, LinkedinIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type FooterProps = {
  language: Language;
  onLanguageChange: (lang: Language) => void;
};
const footerLinks = {
  ua: {
    company: {
      title: 'Компанія',
      links: ['Про нас', 'Якість', 'Клієнти', "Кар'єра"]
    },
    materials: {
      title: 'Матеріали',
      links: ['Каталоги', 'Завантажити', 'Відео', 'Інструкції']
    },
    partners: {
      title: 'Партнерам',
      links: ['Партнери', 'Стати партнером', 'B2B портал', 'Підтримка']
    },
    contact: {
      title: 'Контакти',
      email: 'info@chamaleon.de',
      phone: '+49 123 456 7890',
      address: 'Німеччина'
    }
  },
  en: {
    company: {
      title: 'Company',
      links: ['About Us', 'Quality', 'Clients', 'Careers']
    },
    materials: {
      title: 'Materials',
      links: ['Catalogs', 'Downloads', 'Video', 'Instructions']
    },
    partners: {
      title: 'For Partners',
      links: ['Partners', 'Become a Partner', 'B2B Portal', 'Support']
    },
    contact: {
      title: 'Contact',
      email: 'info@chamaleon.de',
      phone: '+49 123 456 7890',
      address: 'Germany'
    }
  }
};
const socialLinks = [{
  icon: FacebookIcon,
  href: '#',
  label: 'Facebook'
}, {
  icon: InstagramIcon,
  href: '#',
  label: 'Instagram'
}, {
  icon: YoutubeIcon,
  href: '#',
  label: 'YouTube'
}, {
  icon: LinkedinIcon,
  href: '#',
  label: 'LinkedIn'
}];
export function Footer({
  language,
  onLanguageChange
}: FooterProps) {
  const t = footerLinks[language];
  return <footer className="relative bg-chamaleon-black pt-16 pb-8 overflow-hidden" data-id="element-69">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-chamaleon-orange/30 to-transparent" data-id="element-70" />

      {/* Background */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
      backgroundSize: '100px 100px'
    }} data-id="element-71" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-72">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12 mb-12" data-id="element-73">
          {/* Logo & Social */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-8 lg:mb-0" data-id="element-74">
            <a href="#" className="inline-flex items-center gap-2 mb-6" data-id="element-75">
              <div className="w-10 h-10 bg-gradient-to-br from-chamaleon-orange to-chamaleon-orange-dark rounded-lg flex items-center justify-center" data-id="element-76">
                <span className="text-white font-bold text-xl" data-id="element-77">C</span>
              </div>
              <span className="text-white font-bold text-xl tracking-tight" data-id="element-78">
                CHAMALEON
              </span>
            </a>

            {/* Social Links */}
            <div className="flex items-center gap-3" data-id="element-79">
              {socialLinks.map(social => {
              const Icon = social.icon;
              return <motion.a key={social.label} href={social.href} aria-label={social.label} whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} className="w-10 h-10 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center text-gray-400 hover:text-chamaleon-orange hover:border-chamaleon-orange/30 transition-all" data-id="element-80">
                    <Icon className="w-5 h-5" data-id="element-81" />
                  </motion.a>;
            })}
            </div>
          </div>

          {/* Company */}
          <div data-id="element-82">
            <h4 className="text-white font-semibold mb-4" data-id="element-83">{t.company.title}</h4>
            <ul className="space-y-3" data-id="element-84">
              {t.company.links.map(link => <li key={link} data-id="element-85">
                  <a href="#" className="text-gray-400 hover:text-chamaleon-orange transition-colors text-sm" data-id="element-86">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Materials */}
          <div data-id="element-87">
            <h4 className="text-white font-semibold mb-4" data-id="element-88">
              {t.materials.title}
            </h4>
            <ul className="space-y-3" data-id="element-89">
              {t.materials.links.map(link => <li key={link} data-id="element-90">
                  <a href="#" className="text-gray-400 hover:text-chamaleon-orange transition-colors text-sm" data-id="element-91">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Partners */}
          <div data-id="element-92">
            <h4 className="text-white font-semibold mb-4" data-id="element-93">
              {t.partners.title}
            </h4>
            <ul className="space-y-3" data-id="element-94">
              {t.partners.links.map(link => <li key={link} data-id="element-95">
                  <a href="#" className="text-gray-400 hover:text-chamaleon-orange transition-colors text-sm" data-id="element-96">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Contact */}
          <div data-id="element-97">
            <h4 className="text-white font-semibold mb-4" data-id="element-98">{t.contact.title}</h4>
            <ul className="space-y-3" data-id="element-99">
              <li data-id="element-100">
                <a href={`mailto:${t.contact.email}`} className="flex items-center gap-2 text-gray-400 hover:text-chamaleon-orange transition-colors text-sm" data-id="element-101">
                  <MailIcon className="w-4 h-4" data-id="element-102" />
                  {t.contact.email}
                </a>
              </li>
              <li data-id="element-103">
                <a href={`tel:${t.contact.phone}`} className="flex items-center gap-2 text-gray-400 hover:text-chamaleon-orange transition-colors text-sm" data-id="element-104">
                  <PhoneIcon className="w-4 h-4" data-id="element-105" />
                  {t.contact.phone}
                </a>
              </li>
              <li data-id="element-106">
                <span className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-107">
                  <MapPinIcon className="w-4 h-4" data-id="element-108" />
                  {t.contact.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5" data-id="element-109">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-110">
            {/* Copyright */}
            <p className="text-gray-500 text-sm" data-id="element-111">
              © {new Date().getFullYear()} CHAMALEON.{' '}
              {language === 'ua' ? 'Усі права захищено.' : 'All rights reserved.'}
            </p>

            {/* Language Switcher */}
            <div className="flex items-center gap-2" data-id="element-112">
              <button onClick={() => onLanguageChange('ua')} className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${language === 'ua' ? 'bg-chamaleon-orange text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-113">
                UA
              </button>
              <button onClick={() => onLanguageChange('en')} className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${language === 'en' ? 'bg-chamaleon-orange text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-114">
                EN
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}